<template>
    <section :style="{ backgroundColor: bgColor }">
        <div class="px-4 pt-10 mx-auto w-full max-w-6xl lg:pt-20 xl:px-16">

            <h2 class="max-w-2xl text-xl text-center xl:text-3xl md:text-2xl font-ww-bold xl:pr-12 md:text-left" v-html="mainHeading"></h2>

            <div class="mt-8 text-base xl:text-xl md:text-lg font-ww-regular xl:pr-20" v-html="introText"></div>

            <div class="mt-10 mb-6 text-center lg:mt-20 md:mb-12">
                <h3 class="text-2xl text-center text-black xl:leading-10 font-ww-bold xl:text-42 md:text-3xl xl:px-8"
                    v-html="breakdownTitle"></h3>
                <div class="mt-1 text-xl md:mt-3 xl:text-3xl md:text-2xl font-ww-bold" v-html="breakdownSubtitle"></div>
            </div>

            <div class="grid gap-y-6 mb-6 md:mb-12 md:grid-cols-12">
                <!-- Left Column: Separate Ingredients -->
                <div class="space-y-2 md:border-r md:border-[#AEACA6] lg:pr-8 md:pr-4 md:col-span-7 col-span-12">
                    <div class="text-base font-ww-regular md:text-lg !mb-8 !leading-6 md:h-[60px] xl:h-auto" v-html="leftColumnIntro"></div>

                      <div v-for="(item, index) in separateIngredients" :key="index"
                        class="flex justify-between items-start px-4 py-3 bg-white lg:px-6"
                        :class="{ 'items-start': (item as any).note }">
                        <div class="flex flex-col flex-1">
                            <span class="font-lg font-ww-regular" v-html="(item as any).name"></span>
                            <div 
                                v-if="(item as any).note && (item as any).notePosition === 'left'" 
                                class="mt-1 text-sm md:mt-3"
                                v-html="(item as any).note"
                            ></div>
                        </div>
                        <div class="flex flex-col justify-end">
                           <div class="flex justify-end">
                            <span v-if="(item as any).approx" class="mr-1 font-ww-regular font-lg">{{ (item as any).approx }}</span>
                            <span class="font-ww-bold font-lg">{{ (item as any).price }}</span>
                           </div>
                            <div 
                                v-if="(item as any).note && (item as any).notePosition === 'right'" 
                                class="mt-1 text-sm"
                                v-html="(item as any).note"
                            ></div>
                        </div>
                    </div>

                    <div class="lg:py-4 py-2 lg:px-6 px-4 md:!mt-16 !mt-6 bg-white border-2 border-[#F1594B]">
                        <div class="flex justify-between items-center">
                            <span class="text-xl xl:text-3xl md:text-2xl font-ww-bold">Total</span>
                            <span class="text-3xl font-bold leading-none xl:text-45 md:text-4xl font-ww-medium" v-html="separateTotal"></span>
                        </div>
                    </div>
                </div>

                <!-- Right Column: Mindflow -->
                <div class="col-span-12 space-y-2 md:pl-4 md:col-span-5 lg:pl-8">
                    <h3 class="mt-5 xl:mb-5 mb-3 text-2xl text-center md:text-right xl:text-4xl md:text-3xl font-ww-bold md:h-[60px] xl:h-auto lg:relative lg:-top-3" v-html="rightColumnTitle"></h3>

                    <div v-for="(item, index) in mindflowIngredients" :key="index"
                        class="flex justify-between items-center px-4 py-3 bg-white lg:px-6">
                        <span class="font-lg font-ww-regular" v-html="(item as any).name"></span>
                        <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="9.5" cy="9.5" r="9.5" fill="#F1574B" />
                            <path
                                d="M8.12517 12.324L5.61823 9.8171C5.51267 9.71155 5.51267 9.52682 5.61823 9.39488L6.01406 8.99905C6.11962 8.89349 6.30434 8.89349 6.43628 8.99905L8.33628 10.899L12.5585 6.67682C12.6641 6.57127 12.8488 6.57127 12.9807 6.67682L13.3766 7.07266C13.4821 7.17821 13.4821 7.36293 13.3766 7.49488L8.5474 12.324C8.41545 12.4296 8.23073 12.4296 8.12517 12.324Z"
                                fill="white" />
                        </svg>

                    </div>

                    <div class="!mt-6 text-center md:!mt-2">
                        <div class="mb-3 text-2xl font-ww-bold" v-html="priceLabel"></div>
                    </div>

                    <div class="relative lg:py-3.5 py-2 lg:px-6 px-4  xl:!mt-6 lg:!mt-7 md:!mt-6 !mt-3 bg-white border-2 border-[#F1594B]">
                        <div class="flex justify-between items-center">
                            <span class="text-xl xl:text-3xl md:text-2xl font-ww-bold">Total</span>
                            <div class="flex items-center text-right">
                                <div class="relative">
                                    <span class="pt-8 pb-5 pl-10 text-3xl font-bold leading-none xl:text-45 md:text-4xl font-ww-medium" v-html="mindflowPrice"></span>
                                    <nuxt-img v-if="circlePriceImageSrc" :src="circlePriceImageSrc"
                                        class="absolute -right-4 -bottom-5 md:-bottom-7" :alt="circlePriceImageAlt" width="196"
                                        height="109" format="webp" loading="lazy" />
                                </div>
                                <span class="text-xl xl:text-3xl md:text-2xl font-ww-bold" v-html="priceUnit"></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mb-8 text-center">
                <div class="mx-auto max-w-2xl text-base xl:text-lg font-ww-regular xl:px-0" v-html="closingText"></div>
            </div>

            <div class="flex justify-center pb-20">
                <button @click="scrollTo(scrollTarget, 'bottom')" class="flex gap-x-4 justify-center items-center px-4 py-3.5 mx-auto w-10/12 text-lg font-bold text-white rounded-full btn-red sm:w-7/12 md:text-2xl font-ww-medium" :style="{ backgroundColor: buttonColor }">
                    {{ ctaButtonText }}
                    <span>
                        <svg width="21" height="15" viewBox="0 0 21 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.8593 0.338767C12.7655 0.43173 12.6911 0.542331 12.6404 0.66419C12.5896 0.78605 12.5635 0.916755 12.5635 1.04877C12.5635 1.18078 12.5896 1.31148 12.6404 1.43334C12.6911 1.5552 12.7655 1.6658 12.8593 1.75877L17.4593 6.35877L1.05859 6.17123C0.793377 6.17123 0.539023 6.27659 0.351487 6.46412C0.163951 6.65166 0.0585938 6.90601 0.0585938 7.17123C0.0585938 7.43645 0.163951 7.6908 0.351487 7.87834C0.539023 8.06587 0.793377 8.17123 1.05859 8.17123L17.4393 8.35877L12.8593 12.9288C12.673 13.1161 12.5685 13.3696 12.5685 13.6338C12.5685 13.898 12.673 14.1514 12.8593 14.3388C13.0466 14.525 13.3001 14.6296 13.5643 14.6296C13.8284 14.6296 14.0819 14.525 14.2693 14.3388L20.6293 7.97877C20.7149 7.89664 20.783 7.79805 20.8295 7.68892C20.876 7.5798 20.9 7.4624 20.9 7.34377C20.9 7.22514 20.876 7.10773 20.8295 6.99861C20.783 6.88949 20.7149 6.7909 20.6293 6.70877L14.2793 0.338767C14.1863 0.245039 14.0757 0.170645 13.9538 0.119876C13.832 0.0691072 13.7013 0.0429688 13.5693 0.0429688C13.4372 0.0429688 13.3065 0.0691072 13.1847 0.119876C13.0628 0.170645 12.9522 0.245039 12.8593 0.338767Z" fill="white"/>
                        </svg>
                    </span>
                </button>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { scrollTo } from '~/composables/scrollTo'

const props = defineProps({
    bgColor: {
        type: String,
        default: '#f5f1e8'
    },
    mainHeading: {
        type: String,
        default: 'What\'s More Energy, More Productivity, and a More Vibrant Day Worth to You?'
    },
    introText: {
        type: String,
        default: 'You deserve to live the fullest, best life possible.<br><br>That starts with improving what you put in your body every day.<br><br>Upgrade your mind–and life–with Mindflow.<br><br>Here\'s what it would cost for you to buy all these ingredients separately–in the same clinical dosages that we use in Mindflow:'
    },
    breakdownTitle: {
        type: String,
        default: 'Ingredient Cost Breakdown:'
    },
    breakdownSubtitle: {
        type: String,
        default: 'What If You Bought Each Ingredient Separately?'
    },
    leftColumnIntro: {
        type: String,
        default: 'Let\'s do some math… If you went to Amazon today and purchased each of these ingredients individually, here\'s what you\'d pay:'
    },
     separateIngredients: {
         type: Array,
         default: () => [
             { name: 'Instant Organic Coffee', price: '$20' },
             { name: 'Grass-Fed Collagen', price: '$25' },
             { name: 'Organic Lion\'s Mane Extract', price: '$22' },
             { name: 'Organic Chaga Extract', approx: 'Approx.', price: '$22' },
             { name: 'CognatiQ® Coffee Fruit Extract', note: '(Can\'t purchase separate)', notePosition: 'left', approx: 'Approx.', price: '$40' },
             { name: 'Alpha-GPC', note: 'for non trademarked version', notePosition: 'right', approx: 'Approx.', price: '$25' }
         ]
     },
    separateTotal: {
        type: String,
        default: '$154+'
    },
    rightColumnTitle: {
        type: String,
        default: 'Mindflow'
    },
    mindflowIngredients: {
        type: Array,
        default: () => [
            { name: 'Instant Organic Coffee' },
            { name: 'Grass-Fed Collagen' },
            { name: 'Organic Lion\'s Mane Extract' },
            { name: 'Organic Chaga Extract' },
            { name: 'CognatiQ® Coffee Fruit Extract' },
            { name: 'GeniusPure® Alpha-GPC' },
            { name: 'ElectroPrime® Electrolytes' }
        ]
    },
    checkmarkClass: {
        type: String,
        default: 'lucide lucide-circle w-3 h-3 fill-[#d4785f] text-[#d4785f]'
    },
    priceLabel: {
        type: String,
        default: 'Yours as low as'
    },
    mindflowPrice: {
        type: String,
        default: '$24.99'
    },
    priceUnit: {
        type: String,
        default: '/bag'
    },
    circlePriceImageSrc: {
        type: String,
        default: '/assets/images/circle-price.png'
    },
    circlePriceImageAlt: {
        type: String,
        default: 'Price Highlight'
    },
    closingText: {
        type: String,
        default: 'With Mindflow, you get all of these brain-boosting ingredients in one simple, delicious cup—at a fraction of the cost. Save money, save time, and unlock your brain\'s full potential effortlessly!'
    },
    ctaButtonText: {
        type: String,
        default: 'ORDER NOW'
    },
    ctaButtonClass: {
        type: String,
        default: 'group bg-[#d4785f] hover:bg-[#c4695f] text-white font-bold text-lg px-12 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-3'
    },
    buttonColor: {
        type: String,
        default: '#F1594B'
    },
    scrollTarget: {
        type: String,
        default: 'products'
    }
})



defineBuilderComponent({
    friendlyName: 'Comparison Price Section',
    inputs: [
        {
            name: 'bgColor',
            type: 'color',
            defaultValue: '#f5f1e8'
        },
        {
            name: 'mainHeading',
            type: 'richText',
            defaultValue: 'What\'s More Energy, More Productivity, and a More Vibrant Day Worth to You?'
        },
        {
            name: 'introText',
            type: 'richText',
            defaultValue: 'You deserve to live the fullest, best life possible.<br><br>That starts with improving what you put in your body every day.<br><br>Upgrade your mind–and life–with Mindflow.<br><br>Here\'s what it would cost for you to buy all these ingredients separately–in the same clinical dosages that we use in Mindflow:'
        },
        {
            name: 'breakdownTitle',
            type: 'richText',
            defaultValue: 'Ingredient Cost Breakdown:'
        },
        {
            name: 'breakdownSubtitle',
            type: 'richText',
            defaultValue: 'What If You Bought Each Ingredient Separately?'
        },
        {
            name: 'leftColumnIntro',
            type: 'richText',
            defaultValue: 'Let\'s do some math… If you went to Amazon today and purchased each of these ingredients individually, here\'s what you\'d pay:'
        },
         {
             name: 'separateIngredients',
             type: 'list',
             subFields: [
                 {
                     name: 'name',
                     type: 'text',
                     defaultValue: ''
                 },
                 {
                     name: 'note',
                     type: 'text',
                     defaultValue: ''
                 },
                 {
                     name: 'notePosition',
                     type: 'text',
                     enum: ['left', 'right'],
                     defaultValue: 'right'
                 },
                 {
                     name: 'approx',
                     type: 'text',
                     defaultValue: ''
                 },
                 {
                     name: 'price',
                     type: 'text',
                     defaultValue: ''
                 }
             ],
             defaultValue: [
                 { name: 'Instant Organic Coffee', price: '$20' },
                 { name: 'Grass-Fed Collagen', price: '$25' },
                 { name: 'Organic Lion\'s Mane Extract', price: '$22' },
                 { name: 'Organic Chaga Extract', approx: 'Approx.', price: '$22' },
                 { name: 'CognatiQ® Coffee Fruit Extract', note: '(Can\'t purchase separate)', notePosition: 'left', approx: 'Approx.', price: '$40' },
                 { name: 'Alpha-GPC', note: 'for non trademarked version', notePosition: 'right', approx: 'Approx.', price: '$25' }
             ]
         },
        {
            name: 'separateTotal',
            type: 'text',
            defaultValue: '$154+'
        },
        {
            name: 'rightColumnTitle',
            type: 'richText',
            defaultValue: 'Mindflow'
        },
        {
            name: 'mindflowIngredients',
            type: 'list',
            subFields: [
                {
                    name: 'name',
                    type: 'text',
                    defaultValue: ''
                }
            ],
            defaultValue: [
                { name: 'Instant Organic Coffee' },
                { name: 'Grass-Fed Collagen' },
                { name: 'Organic Lion\'s Mane Extract' },
                { name: 'Organic Chaga Extract' },
                { name: 'CognatiQ® Coffee Fruit Extract' },
                { name: 'GeniusPure® Alpha-GPC' },
                { name: 'ElectroPrime® Electrolytes' }
            ]
        },
        {
            name: 'checkmarkClass',
            type: 'text',
            defaultValue: 'lucide lucide-circle w-3 h-3 fill-[#d4785f] text-[#d4785f]'
        },
        {
            name: 'priceLabel',
            type: 'richText',
            defaultValue: 'Yours as low as'
        },
        {
            name: 'mindflowPrice',
            type: 'text',
            defaultValue: '$24.99'
        },
        {
            name: 'priceUnit',
            type: 'text',
            defaultValue: '/bag'
        },
        {
            name: 'circlePriceImageSrc',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
            defaultValue: '/assets/images/circle-price.png'
        },
        {
            name: 'circlePriceImageAlt',
            type: 'text',
            defaultValue: 'Price Highlight'
        },
        {
            name: 'closingText',
            type: 'richText',
            defaultValue: 'With Mindflow, you get all of these brain-boosting ingredients in one simple, delicious cup—at a fraction of the cost. Save money, save time, and unlock your brain\'s full potential effortlessly!'
        },
        {
            name: 'ctaButtonText',
            type: 'text',
            defaultValue: 'ORDER NOW'
        },
        {
            name: 'ctaButtonClass',
            type: 'text',
            defaultValue: 'group bg-[#d4785f] hover:bg-[#c4695f] text-white font-bold text-lg px-12 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-3'
        },
        {
            name: 'buttonColor',
            type: 'color',
            defaultValue: '#F1594B'
        },
        {
            name: 'scrollTarget',
            type: 'text',
            defaultValue: 'products'
        }
    ]
})
</script>
